import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { type ChatCrmContext, chatCrmLockedBy, chatCrmModeOn } from '@src/components/sco/Chat/chatCrmDraft';
import { CLAIM_CRM_THREAD } from '@src/gql/mutations/ClaimCrmThread';
import { DISCARD_TOOL_DRAFT } from '@src/gql/mutations/DiscardToolDraft';
import { RELEASE_CRM_THREAD } from '@src/gql/mutations/ReleaseCrmThread';
import { SEND_TOOL_MESSAGE } from '@src/gql/mutations/SendToolMessage';
import { UPSERT_TOOL_DRAFT } from '@src/gql/mutations/UpsertToolDraft';
import { CHAT_CRM_ACCOUNT, CRM_CHAT_DRAFT } from '@src/gql/queries/CrmChatDraft';
import { prettyErrorMessage } from '@src/lib/mutationErrors';
import { useEffect, useRef } from 'react';

/** How often an open CRM conversation reads the draft again while the tab is visible (D6). */
export const CRM_CHAT_DRAFT_POLL_MS = 8000;

/**
 * Editing renews the claim at most this often. Saves do not claim on the server (assertReplyOwner only
 * checks), so without renewals a claim taken once would lapse after 15 minutes mid-reply.
 */
export const CRM_CHAT_CLAIM_RENEW_MS = 30000;

/** A save or send. `error` is set when `ok` is false (this app has no strictNullChecks to narrow a union). */
export type CrmChatWrite = {
  ok: boolean;
  commentId?: string | null;
  unchanged?: boolean;
  error?: string;
};

// The writes run in plain module functions: the React Compiler (1.0.0) skips a hook whose try/catch
// holds optional chaining, and this hook is part of the chat composer.
type Mutate = (options: {
  variables: {
    input: Record<string, unknown>;
  };
}) => Promise<{
  data?: unknown;
}>;
async function saveCrmDraft(upsertDraft: Mutate, input: Record<string, unknown>): Promise<CrmChatWrite> {
  try {
    const result = await upsertDraft({
      variables: {
        input
      }
    });
    const payload = (result.data as {
      upsertToolDraft?: {
        comment?: {
          commentId?: string | null;
        } | null;
        unchanged?: boolean;
      };
    })?.upsertToolDraft;
    return {
      commentId: payload?.comment?.commentId || null,
      ok: true,
      unchanged: Boolean(payload?.unchanged)
    };
  } catch (error) {
    return {
      error: prettyErrorMessage(error),
      ok: false
    };
  }
}
async function sendCrmDraft(sendMessage: Mutate, input: Record<string, unknown>): Promise<CrmChatWrite> {
  try {
    const result = await sendMessage({
      variables: {
        input
      }
    });
    const sent = (result.data as {
      sendToolMessage?: {
        comment?: {
          commentId?: string | null;
        } | null;
      };
    })?.sendToolMessage;
    return {
      commentId: sent?.comment?.commentId || null,
      ok: true
    };
  } catch (error) {
    return {
      error: prettyErrorMessage(error),
      ok: false
    };
  }
}
async function discardCrmDraft(discardDraft: Mutate, input: Record<string, unknown>): Promise<CrmChatWrite> {
  try {
    await discardDraft({
      variables: {
        input
      }
    });
    return {
      ok: true
    };
  } catch (error) {
    return {
      error: prettyErrorMessage(error),
      ok: false
    };
  }
}

/**
 * The on-site composer's side of the shared CRM reply draft (crm-chat-draft-sync D4–D7). Only a crm
 * account asks; for anyone else this reads nothing and `on` stays false. While on, the draft is read
 * again every 8 seconds with the tab visible, and on focus. Writes go through the desk's own mutations
 * (upsertToolDraft, sendToolMessage), so the run, the edit tracking and the audit stay right.
 *
 * The claim (L3): editing claims the reply and keeps renewing it (at most every 30 seconds), and leaving
 * the conversation lets it go. With no edits it lapses on the server after 15 minutes.
 */
export function useCrmChatDraft(input) {
  const $ = _c(56);
  const t0 = !input.enabled || !input.userId;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      skip: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const accountQuery = useQuery(CHAT_CRM_ACCOUNT, t1);
  const isCrmAccount = accountQuery.data?.currentUser?.crmEnabled === true;
  const threadId = input.threadId || null;
  const t2 = !input.enabled || !isCrmAccount || !threadId || Boolean(input.parentCommentId);
  const t3 = threadId || "";
  let t4;
  if ($[2] !== t3) {
    t4 = {
      threadId: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t2 || $[5] !== t4) {
    t5 = {
      fetchPolicy: "network-only",
      skip: t2,
      variables: t4
    };
    $[4] = t2;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const draftQuery = useQuery(CRM_CHAT_DRAFT, t5);
  const context = draftQuery.data?.crmChatDraft ?? null;
  let t6;
  if ($[7] !== context || $[8] !== input.parentCommentId || $[9] !== input.userId) {
    t6 = chatCrmModeOn({
      context,
      parentCommentId: input.parentCommentId,
      userId: input.userId
    });
    $[7] = context;
    $[8] = input.parentCommentId;
    $[9] = input.userId;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const on = t6;
  let t7;
  if ($[11] !== context || $[12] !== input.userId || $[13] !== on) {
    t7 = on ? chatCrmLockedBy(context, input.userId) : null;
    $[11] = context;
    $[12] = input.userId;
    $[13] = on;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const lockedBy = t7;
  const [upsertDraft] = useMutation(UPSERT_TOOL_DRAFT);
  const [sendMessage] = useMutation(SEND_TOOL_MESSAGE);
  const [discardDraft] = useMutation(DISCARD_TOOL_DRAFT);
  const [claimThread] = useMutation(CLAIM_CRM_THREAD);
  const [releaseThread] = useMutation(RELEASE_CRM_THREAD);
  const claimedFor = useRef(null);
  const claimedAt = useRef(0);
  const refetch = draftQuery.refetch;
  let t8;
  if ($[15] !== on || $[16] !== refetch) {
    t8 = () => {
      if (!on) {
        return;
      }
      const reread = () => {
        if (typeof document === "undefined" || document.visibilityState === "visible") {
          refetch();
        }
      };
      const timer = setInterval(reread, CRM_CHAT_DRAFT_POLL_MS);
      window.addEventListener("focus", reread);
      return () => {
        clearInterval(timer);
        window.removeEventListener("focus", reread);
      };
    };
    $[15] = on;
    $[16] = refetch;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== on || $[19] !== refetch || $[20] !== threadId) {
    t9 = [on, refetch, threadId];
    $[18] = on;
    $[19] = refetch;
    $[20] = threadId;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  useEffect(t8, t9);
  let t10;
  if ($[22] !== releaseThread) {
    t10 = () => {
      const release = () => {
        const claimed = claimedFor.current;
        if (claimed) {
          claimedFor.current = null;
          releaseThread({
            variables: {
              input: {
                threadId: claimed
              }
            }
          }).catch(_temp);
        }
      };
      window.addEventListener("pagehide", release);
      return () => {
        window.removeEventListener("pagehide", release);
        release();
      };
    };
    $[22] = releaseThread;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== releaseThread || $[25] !== threadId) {
    t11 = [releaseThread, threadId];
    $[24] = releaseThread;
    $[25] = threadId;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  useEffect(t10, t11);
  let t12;
  if ($[27] !== context?.inboundCommentId || $[28] !== context?.seenInboundCommentId || $[29] !== context?.threadId || $[30] !== context?.toolId || $[31] !== threadId) {
    t12 = function ids() {
      return {
        inboundCommentId: context?.inboundCommentId || null,
        seenInboundCommentId: context?.seenInboundCommentId || null,
        threadId: context?.threadId || threadId,
        toolId: context?.toolId || null
      };
    };
    $[27] = context?.inboundCommentId;
    $[28] = context?.seenInboundCommentId;
    $[29] = context?.threadId;
    $[30] = context?.toolId;
    $[31] = threadId;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const ids = t12;
  let t13;
  if ($[33] !== claimThread || $[34] !== input.userId || $[35] !== on || $[36] !== threadId) {
    t13 = function claim() {
      if (!on || !threadId) {
        return;
      }
      const now = Date.now();
      if (claimedFor.current === threadId && now - claimedAt.current < CRM_CHAT_CLAIM_RENEW_MS) {
        return;
      }
      claimedFor.current = threadId;
      claimedAt.current = now;
      claimThread({
        variables: {
          input: {
            accountUserId: input.userId,
            threadId
          }
        }
      }).catch(() => {
        claimedFor.current = null;
        claimedAt.current = 0;
      });
    };
    $[33] = claimThread;
    $[34] = input.userId;
    $[35] = on;
    $[36] = threadId;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  const claim = t13;
  let t14;
  if ($[38] !== ids || $[39] !== upsertDraft) {
    t14 = function save(body, storedFileIds) {
      return saveCrmDraft(upsertDraft, {
        ...ids(),
        body,
        storedFileIds
      });
    };
    $[38] = ids;
    $[39] = upsertDraft;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  const save = t14;
  let t15;
  if ($[41] !== ids || $[42] !== sendMessage) {
    t15 = function send(body_0, storedFileIds_0, commentId) {
      const files = storedFileIds_0 ? {
        storedFileIds: storedFileIds_0
      } : {};
      return sendCrmDraft(sendMessage, {
        ...ids(),
        body: body_0,
        ...files,
        ...(commentId ? {
          commentId
        } : {})
      });
    };
    $[41] = ids;
    $[42] = sendMessage;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  const send = t15;
  let t16;
  if ($[44] !== discardDraft || $[45] !== ids) {
    t16 = function discard() {
      const {
        inboundCommentId,
        threadId: forThread,
        toolId
      } = ids();
      return discardCrmDraft(discardDraft, {
        inboundCommentId,
        threadId: forThread,
        toolId
      });
    };
    $[44] = discardDraft;
    $[45] = ids;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const discard = t16;
  let t17;
  if ($[47] !== claim || $[48] !== context || $[49] !== discard || $[50] !== lockedBy || $[51] !== on || $[52] !== refetch || $[53] !== save || $[54] !== send) {
    t17 = {
      claim,
      context,
      discard,
      lockedBy,
      on,
      refetch,
      save,
      send
    };
    $[47] = claim;
    $[48] = context;
    $[49] = discard;
    $[50] = lockedBy;
    $[51] = on;
    $[52] = refetch;
    $[53] = save;
    $[54] = send;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  return t17;
}
function _temp() {}